import {
  actionCardShownProps,
  buildActionCardProps,
  type ChatPromptCardEntry,
} from '@littlebird/action-cards';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { AppLogo } from '@/assets/icons/AppLogo';
import { Icons } from '@/assets/icons/icons';
import { Button, XStack, YStack } from '@/components/ui';
import {
  placementProps,
  useFeedImpression,
} from '@/features/action-cards-view';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { HomeChatPromptCardPane } from './HomeChatPromptCardPane';

import type { WaitingForYouState } from '../use-waiting-for-you';
import type { ActionCardPlacement } from '@littlebird/sdk';

type HomeChatPromptCardProps = {
  readonly entry: ChatPromptCardEntry;
  readonly itemId: string;
  readonly home: WaitingForYouState;
  // The row the list gave the card, which the server keeps for the feed stats. A filtered list gives none
  readonly placement?: ActionCardPlacement;
  readonly fadeActions?: boolean;
};

export const HomeChatPromptCard = observer(
  ({
    entry,
    itemId,
    home,
    placement,
    fadeActions = false,
  }: HomeChatPromptCardProps) => {
    const expanded = home.isItemExpanded(itemId);
    const { card } = entry;
    const strip = home.deferredDismiss.stripForCard(card.id);
    const deferred = strip !== undefined;
    const subject = entry.content.title;
    const provenance = home.getProvenance(card);
    const showDismiss = !deferred && !expanded;

    useEffect(() => {
      if (deferred) return;
      const properties = actionCardShownProps(
        entry.card,
        {
          surface: 'home',
          card_expanded: expanded,
        },
        'full'
      );
      if (properties) {
        trackEvent('ACTION_CARD_SHOWN', {
          ...card.analytics,
          ...properties,
          experiment_id: entry.content.experiment_id,
          ...placementProps(placement),
        });
      }
    }, [
      card,
      deferred,
      entry.card,
      entry.content.experiment_id,
      expanded,
      placement,
    ]);
    const impressionRef = useFeedImpression(
      card,
      placement?.surface ?? 'home',
      placement
    );

    if (deferred) return null;

    const toggleExpanded = () => {
      const nextExpanded = !expanded;
      home.setItemExpanded(itemId, nextExpanded);
      trackEvent('ACTION_CARD_EXPANDED', {
        ...card.analytics,
        ...buildActionCardProps(card, {
          surface: 'home',
          card_expanded: nextExpanded,
        }),
        expanded: nextExpanded,
      });
    };

    return (
      <YStack
        ref={impressionRef}
        className={fadeActions ? 'group/feed-card' : undefined}
      >
        <XStack className="group/action-row relative min-h-10 items-center gap-2 rounded-md px-2 py-2">
          <button
            type="button"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${subject}`}
            aria-expanded={expanded}
            onClick={toggleExpanded}
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent',
              deferred && 'grayscale opacity-70'
            )}
          >
            <AppLogo size={24} className="size-6" />
          </button>
          <XStack
            className="min-w-0 flex-1 cursor-default items-center gap-2 rounded-md outline-none"
            onClick={toggleExpanded}
          >
            <XStack className="min-w-0 flex-1 items-baseline gap-1.5">
              <span
                data-find-field="summary"
                className={cn(
                  'max-w-[60%] shrink-0 truncate text-sm font-medium text-foreground-subtle transition-colors',
                  deferred
                    ? 'line-through text-foreground-muted'
                    : 'group-hover/action-row:text-foreground group-focus-within/action-row:text-foreground dark:group-hover/action-row:text-white dark:group-focus-within/action-row:text-white'
                )}
              >
                {subject}
              </span>
              <span
                data-find-field="action"
                className="shrink-0 text-xs text-foreground-muted transition-colors group-hover/action-row:text-foreground group-focus-within/action-row:text-foreground"
              >
                Start chat
              </span>
            </XStack>
            <span
              data-find-exclude
              className={cn(
                'shrink-0 text-[0.6875rem] tabular-nums text-foreground-muted',
                showDismiss &&
                  'group-hover/action-row:invisible group-focus-within/action-row:invisible [@media(hover:none)]:invisible'
              )}
            >
              {provenance.ageLabel}
            </span>
          </XStack>
          {showDismiss ? (
            <Button
              variant="outline"
              size="dense"
              iconOnly
              className="absolute right-2 opacity-0 group-hover/action-row:opacity-100 group-focus-within/action-row:opacity-100 [@media(hover:none)]:opacity-100"
              aria-label={`Dismiss ${subject}`}
              onClick={() => home.deferDismiss([entry])}
            >
              <Icons.cancel aria-hidden="true" />
            </Button>
          ) : null}
        </XStack>
        {expanded ? (
          <YStack className="pb-3 pt-1">
            <HomeChatPromptCardPane
              entry={entry}
              home={home}
              fadeActions={fadeActions}
            />
          </YStack>
        ) : null}
      </YStack>
    );
  }
);
